/**
 * SRR-2582 — Post-authentication landing redirect.
 *
 * The Dashboard is now the default landing page after authentication for every
 * user type that has access to it, replacing the temporary Campaigns default
 * introduced by PD-4676 while the Dashboard refactor was still being tested in
 * production.
 *
 * Dashboard eligibility mirrors the SRR-2595 rules applied to `user/active_dashboard`
 * in store/modules/user.js (see hasDashboardAccess below) — user types without
 * Dashboard access still land on Campaigns.
 *
 * The Graphic Designer role is a separate, unrelated special case (PD-4678) that
 * lands on the Social Boost Campaigns section instead, so it is short-circuited
 * before the Dashboard-access check.
 *
 * The user-type slug, publishers list, and parent are passed in explicitly (from the
 * login response payload, e.g. data.user_data.type.slug / data.user_data.publishers /
 * buildParentUser(data.user_data)) so the redirect does not depend on the store having
 * been hydrated yet — it stays correct even if auth/LOGIN later becomes asynchronous.
 *
 * PD-4914 — when the auth middleware recorded an intended destination (an
 * unauthenticated user hit a specific URL and was bounced to login with
 * ?redirect=<path>), that destination wins over the user-type default landing
 * below, so deep links survive the login round-trip.
 */

import * as keys from '@/router/router-keys'

/**
 * Builds the merged "parent account" object that isTopBrandAdmin/hasAgencyParent below
 * expect. Three differently-shaped notions of "the parent" exist in the login payload:
 * `userData.parent` (id/name/email/user_id/cpms/type/parent_type) and
 * `userData.type.parent_user` (slug/title/...SUBSCRIPTION_BASE) — isTopBrandAdmin needs
 * the former, hasAgencyParent needs the latter. Mirrors the merge expression at
 * store/modules/user.js:133 exactly (that file imports and calls this same function)
 * so the two cannot drift apart again.
 *
 * Keep the `null` branch — do not simplify to an unconditional spread. `{}` is truthy,
 * so isTopBrandAdmin's `!parent` guard would return `false` for every top-level
 * brand-admin/brand-employee and re-break the SRR-2582 `f23d4fa1d` fix.
 *
 * @param {?object} userData - the login response's user_data (or store INIT payload)
 * @returns {?object} the merged parent account, or null if the user has none
 */
export const buildParentUser = (userData) =>
  userData?.type?.parent_user || userData?.parent ? { ...userData.type?.parent_user, ...userData.parent } : null

// User-type slug that lands on the Social Boost Campaigns section.
export const SOCIAL_BOOST_LANDING_TYPE = 'graphic-designer'

// User-type slugs that never have Dashboard access, regardless of publishers.
const NO_DASHBOARD_ACCESS_TYPES = ['agency-client', SOCIAL_BOOST_LANDING_TYPE]

/**
 * Whether a brand-admin/brand-employee is "top-level" for Dashboard access purposes.
 * Mirrors `$isTopBrandAdmin()` in plugins/roleAndAccess.js, which the sidebar menu
 * uses to bypass `active_dashboard` entirely (see store/modules/user.js). Duplicated
 * here (rather than imported) because this runs before the store is hydrated.
 *
 * @param {?string} slug - the user type slug
 * @param {?object} parent - the user's merged parent account, if any (buildParentUser above)
 * @returns {boolean}
 */
const isTopBrandAdmin = (slug, parent) => {
  const isBrandAdmin = slug === 'brand-admin' && !parent
  const isBrandEmployee = slug === 'brand-employee' && !parent
  const isTrafficManagerFromParent = parent?.type === 'traffic-manager'
  const isTrafficManagerFromGrandParent = parent?.parent_type === 'traffic-manager'

  return isBrandAdmin || isBrandEmployee || isTrafficManagerFromParent || isTrafficManagerFromGrandParent
}

/**
 * Whether a user of the given type has access to the Dashboard section.
 * The `active_dashboard` rules live here; store/modules/user.js imports and calls
 * this function directly (rather than re-implementing it), so there is only one
 * implementation to keep in sync.
 *
 * Deliberately does NOT account for the `$isTopBrandAdmin()` bypass — store/modules/
 * user.js also calls this function (2-arg, no `parent`) to compute `active_dashboard`,
 * and relies on menu.vue ORing `$isTopBrandAdmin()` in separately at the template
 * level. Baking the bypass in here would silently flip `active_dashboard` to `true`
 * for every brand-admin/brand-employee at that call site (since a missing `parent`
 * arg is `undefined`, and `!undefined` is `true`), regressing SRR-2595. Callers that
 * need the bypass (i.e. the redirect below) must check `isTopBrandAdmin` themselves.
 *
 * @param {?string} slug - the user type slug
 * @param {?Array} publishers - the user's assigned publishers, if any
 * @returns {boolean}
 */
export const hasDashboardAccess = (slug, publishers) => {
  if (NO_DASHBOARD_ACCESS_TYPES.includes(slug)) return false

  // A brand-admin only loses Dashboard access once they have a publisher assigned
  // (i.e. they are not a top-level brand-admin). See SRR-2595.
  if (slug === 'brand-admin') return !(Array.isArray(publishers) && publishers.length > 0)

  return true
}

// User-type slugs excepted from the agency-parent Dashboard-menu restriction below —
// mirrors the `['agency-employee', 'brand-employee'].includes(user_type)` exception
// in components/sidebar/menu.vue:15.
const AGENCY_PARENT_DASHBOARD_EXCEPTIONS = ['agency-employee', 'brand-employee']

/**
 * Whether the user's parent account is an agency. Mirrors `hasAgencyParent` in
 * components/sidebar/menu.vue (`state.user.parent_user && state.user.parent_user.slug
 * === 'agency'`), duplicated here for the same reason as `isTopBrandAdmin` above.
 * `slug` only exists on the `type.parent_user` half of the payload, so `parent` here
 * must be the merged object from `buildParentUser`, not the raw login-payload `parent`.
 *
 * @param {?object} parent - the user's merged parent account, if any (buildParentUser above)
 * @returns {boolean}
 */
const hasAgencyParent = (parent) => Boolean(parent) && parent.slug === 'agency'

/**
 * Whether the user would see the Dashboard entry in the sidebar menu, so the redirect
 * cannot send someone to a section their own navigation hides — see
 * components/sidebar/menu.vue:15 (kept in sync with this function; a `// keep in sync
 * with utils/postAuthRedirect.js` comment lives on that line too). Does not reproduce
 * the `$canAccess([], ['publisher', 'traffic-manager'])` term — publishers/traffic-
 * managers never reach this check because middleware/modules/auth.js:62-66 bounces
 * them to social-boost before the redirect runs, regardless of Dashboard access.
 *
 * @param {?string} slug - the user type slug
 * @param {?Array} publishers - the user's assigned publishers, if any
 * @param {?object} parent - the user's merged parent account, if any (buildParentUser above)
 * @returns {boolean}
 */
const canSeeDashboardMenu = (slug, publishers, parent) => {
  if (isTopBrandAdmin(slug, parent)) return true
  if (!hasDashboardAccess(slug, publishers)) return false

  return !hasAgencyParent(parent) || AGENCY_PARENT_DASHBOARD_EXCEPTIONS.includes(slug)
}

/**
 * PD-4914 — validates the `?redirect=` intended destination recorded by
 * middleware/modules/auth.js before it is used as a post-authentication target.
 *
 * Only root-relative, same-origin paths are accepted, so a crafted link cannot
 * bounce a freshly authenticated user off-site. Protocol-relative targets
 * ("//evil.example"), absolute URLs and backslash forms (which some browsers
 * normalise to "/") are all rejected. The value arrives already decoded by
 * vue-router, so "%2F%2Fevil.example" is caught by the "//" check rather than
 * slipping past it, and double-encoded forms fail the leading-"/" check.
 *
 * The candidate is then resolved through the router and rejected unless it
 * matches a named route outside the auth layout. That drops two cases the
 * string checks alone let through: "/login" (which would bounce a just-
 * authenticated user back to the login screen, self-healing only by accident of
 * the guest guard) and unmatched paths (which would land on the 404 route). The
 * only legitimate producer of this query is the auth middleware, and it only
 * ever records auth-guarded application routes, so nothing legitimate is lost.
 *
 * @param {import("vue-router").default} router - the app router instance, used to resolve the candidate
 * @param {*} target - the raw query value
 * @returns {?string} the safe path, or null when it must not be used
 */
export const sanitizeRedirect = (router, target) => {
  if (typeof target !== 'string' || target.length === 0) return null
  if (!target.startsWith('/') || target.startsWith('//')) return null
  if (target.includes('\\')) return null

  const resolved = router.resolve(target).route

  if (!resolved.name || resolved.meta.layout === 'auth-layout') return null

  return target
}

/**
 * Push the user to their post-authentication landing route.
 *
 * `intendedPath` is passed in explicitly rather than read off `router.currentRoute`
 * for the same reason as `slug`/`publishers`/`parent` (see the module docblock):
 * the redirect must not depend on ambient state. Only Login.vue supplies it — it is
 * the sole route reachable with a `?redirect=` query, since middleware/modules/auth.js
 * is the only producer and it always pushes to `login`.
 *
 * @param {import("vue-router").default} router - the app router instance ($router)
 * @param {?string} slug - the authenticated user's type slug (data.user_data?.type?.slug)
 * @param {?Array} publishers - the authenticated user's assigned publishers (data.user_data?.publishers)
 * @param {?object} parent - the authenticated user's merged parent account, if any (buildParentUser(data.user_data))
 * @param {?string} intendedPath - the raw `?redirect=` value recorded by the auth middleware ($route.query.redirect)
 */
const redirectAfterAuth = (router, slug, publishers, parent, intendedPath) => {
  // PD-4914 — an intended destination recorded by the auth middleware wins over
  // the user-type default landing. The target is re-evaluated by the route guards
  // on arrival, so this cannot reach a section the user is not allowed to see:
  // middleware/modules/auth.js bounces them exactly as it would if they had typed
  // the URL, which is why this may short-circuit the landing policy above.
  const intended = sanitizeRedirect(router, intendedPath)

  if (intended) {
    // Stand down the expired-session restore in router/index.js:48-63. It clears
    // these keys only when landing on `dashboard` from `login`, which is precisely
    // the navigation this branch replaces — left set, they would silently teleport
    // the user to a stale route on some later, unrelated login.
    sessionStorage.setItem(keys.LS_ROUTE_EXPIRED_SESSION, JSON.stringify(false))
    sessionStorage.setItem(keys.LS_ROUTE_KEY, '')

    router.push(intended).catch(() => {})

    return
  }

  let routeName = 'dashboard'

  if (slug === SOCIAL_BOOST_LANDING_TYPE) {
    // Graphic Designers land on the Social Boost Campaigns section.
    routeName = 'social-boost'
  } else if (!canSeeDashboardMenu(slug, publishers, parent)) {
    // Anyone whose sidebar hides the Dashboard entry (see canSeeDashboardMenu above)
    // lands on Campaigns instead — same as before the SRR-2582 default flip.
    routeName = 'campaigns'
  }

  // router.push returns a promise that rejects on redundant/aborted
  // navigation (vue-router NavigationDuplicated). Swallow it so it does not
  // surface as an unhandled rejection.
  router.push({ name: routeName }).catch(() => {})
}

export default redirectAfterAuth
